
import React, { useState } from "react";

const OrderChargesSettings = ({
  orderCharges,
  packagingState,
  setPackagingState,
  handleToggle,
  handleUpdatePackaging,
}) => {
  const [removingPackaging, setRemovingPackaging] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);

  const handleRemovePackagingCharges = async () => {
    const removedState = {
      ...packagingState,
      orderWise: false,
      itemWise: false,
      orderAmount: 0,
      itemAmount: 0,
    };

    try {
      setRemovingPackaging(true);

      setPackagingState(removedState);

      await handleUpdatePackaging(removedState);

      setPackagingState(removedState);

      setShowRemoveConfirmModal(false);
    } catch (error) {
      console.error(" REMOVE PACKAGING ERROR:", error);
    } finally {
      setRemovingPackaging(false);
    }
  };

  return (
    <>
      <div className="grid lg:grid-cols-3 gap-6 montserrat-font-1">
        {/* LEFT SIDE */}
        <div className="lg:col-span-2 grid md:grid-cols-2 gap-4">
          {orderCharges?.map((charge, i) => (
            <div key={i} className="bg-white border rounded-xl p-5 shadow-sm">
              <h2 className="text-green-700 font-semibold mb-3">
                Currently Applied Order Charges
              </h2>

              <div className="border-t pt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Charges:</span>
                  <span>
                    {charge.charge_type === "fixed" ? "₹" : ""}
                    {parseFloat(charge.charges || 0).toFixed(2)}
                    {charge.charge_type === "percentage" ? "%" : ""}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>From:</span>
                  <span>₹{parseFloat(charge.from_amount || 0).toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>To:</span>
                  <span>₹{parseFloat(charge.to_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT SIDE */}
        <div className="bg-white p-6 rounded-2xl h-fit">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-green-700 font-semibold">Packaging Charges</h2>
            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">
              chaafo
            </span>
          </div>

          {/* ORDER WISE */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm font-medium">Order-wise Packaging</p>
              <p className="text-xs text-gray-500">Applies once per order</p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={Boolean(packagingState.orderWise)}
                onChange={() => handleToggle("orderWise")}
              />
              <div
                className="w-11 h-6 bg-gray-300 rounded-full peer 
                peer-checked:bg-green-600 
                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                after:bg-white after:rounded-full after:h-5 after:w-5 
                after:transition-all peer-checked:after:translate-x-full"
              ></div>
            </label>
          </div>

          {packagingState.orderWise && (
            <div className="mb-4">
              <label className="text-sm text-gray-700">
                Order Packaging Charge (₹)
              </label>
              <input
                type="number"
                value={packagingState.orderAmount}
                onChange={(e) =>
                  setPackagingState((prev) => ({
                    ...prev,
                    orderAmount: e.target.value,
                  }))
                }
                className="w-full mt-1 px-3 py-2 border rounded-lg"
              />
            </div>
          )}

          <hr className="my-3" />

          {/* ITEM WISE */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm font-medium">Item-wise Packaging</p>
              <p className="text-xs text-gray-500">Applies on each item</p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={Boolean(packagingState.itemWise)}
                onChange={() => handleToggle("itemWise")}
              />
              <div
                className="w-11 h-6 bg-gray-300 rounded-full peer 
                peer-checked:bg-green-600 
                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                after:bg-white after:rounded-full after:h-5 after:w-5 
                after:transition-all peer-checked:after:translate-x-full"
              ></div>
            </label>
          </div>

          {packagingState.itemWise && (
            <div className="mb-4">
              <label className="text-sm text-gray-700">
                Item Packaging Charge (₹)
              </label>
              <input
                type="number"
                value={packagingState.itemAmount}
                onChange={(e) =>
                  setPackagingState((prev) => ({
                    ...prev,
                    itemAmount: e.target.value,
                  }))
                }
                className="w-full mt-1 px-3 py-2 border rounded-lg"
              />
            </div>
          )}

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleUpdatePackaging()}
              className="w-full bg-green-700 text-white py-2 rounded-lg"
            >
              Update Packaging Settings
            </button>

            <button
              type="button"
              onClick={() => setShowRemoveConfirmModal(true)}
              disabled={removingPackaging}
              className={`w-full border py-2 rounded-lg transition ${
                removingPackaging
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                  : "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
              }`}
            >
              {removingPackaging
                ? "Removing Packaging Charges..."
                : "Remove Packaging Charges"}
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            Only one option can be enabled at a time.
          </p>
        </div>
      </div>

      {/* REMOVE CONFIRMATION MODAL */}
      {showRemoveConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4 montserrat-font-1">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b px-6 py-5">
              <h2 className="text-lg font-semibold text-gray-900">
                Remove Packaging Charges?
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                This will turn off both order-wise and item-wise packaging
                charges and set both amounts to ₹0.
              </p>
            </div>

            <div className="px-6 py-5">
              <div className="rounded-xl border border-red-100 bg-red-50 p-4">
                <p className="text-sm font-medium text-red-700">
                  Are you sure you want to remove packaging charges?
                </p>
                <p className="mt-1 text-xs text-red-500">
                  After confirmation, packaging charge will not be added
                  item-wise or order-wise.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowRemoveConfirmModal(false)}
                disabled={removingPackaging}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleRemovePackagingCharges}
                disabled={removingPackaging}
                className={`rounded-lg px-4 py-2 text-sm text-white transition ${
                  removingPackaging
                    ? "cursor-not-allowed bg-red-300"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {removingPackaging ? "Removing..." : "Yes, Remove Charges"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderChargesSettings;